// Shared constants for pulse components. Ported from qa-live's
// apiConfig.ts (PODS) and appConfig.ts (SPRINT_END_DATES, LEADERSHIP_AUTHORIZED_USERS,
// QA_SCORE_PENALTIES, QA_SCORE_THRESHOLDS).

export const PODS = {
    BUYER: { key: 'BP', boardId: 314, name: 'Buyer' },
    SELLER: { key: 'SP', boardId: 315, name: 'Seller' },
    FOUNDATIONS: { key: 'FP', boardId: 316, name: 'Foundations' },
}

export const POD_NAMES = Object.fromEntries(Object.values(PODS).map((p) => [p.key, p.name]))

export const POD_FILTERS = [
    { label: 'All Pods', value: 'all', emoji: '🏢' },
    { label: 'Buyer', value: 'Buyer', emoji: '🛒' },
    { label: 'Seller', value: 'Seller', emoji: '🏪' },
    { label: 'Foundations', value: 'Foundations', emoji: '🏗️' },
]

export const TIME_RANGE_OPTIONS = [
    { label: 'Last 1 Month', value: '1m' },
    { label: 'Last 3 Months', value: '3m' },
    { label: 'Last 6 Months', value: '6m' },
    { label: 'Last 12 Months', value: '12m' },
    { label: 'Current Sprint', value: 'current_sprint' },
]

// Sprint end dates keyed by sprint number — sourced from Jira BP board 314.
// Used to filter to sprints that have already started (isValidSprint).
export const SPRINT_END_DATES = {
    1: '2025-10-22',
    2: '2025-11-05',
    3: '2025-11-19',
    4: '2025-12-03',
    5: '2025-12-17',
    6: '2025-12-31',
    7: '2026-01-14',
    8: '2026-01-28',
    9: '2026-02-11',
    10: '2026-02-25',
    11: '2026-03-11',
    12: '2026-03-25',
    13: '2026-04-08',
    14: '2026-04-22',
    15: '2026-05-06',
    16: '2026-05-20',
    17: '2026-06-03',
    18: '2026-06-17',
    19: '2026-07-01',
    20: '2026-07-15',
    21: '2026-07-29',
    22: '2026-08-12',
    23: '2026-08-26',
    24: '2026-09-09',
    25: '2026-09-23',
    26: '2026-10-07',
}

// Users who can see the Impact tab (Champions/Leaderboard).
export const LEADERSHIP_AUTHORIZED_USERS = ['sohil@bstock.com', 'ken.shum@bstock.com', 'alvaro@bstock.com']

// QA team roster with US/Evizi sub-team assignment. Ported from qa-live appConfig.ts
// (QA-2286) — the server keeps a parallel QA_TEAM_MEMBER_PROFILES list without `team`.
// Used by the Impact cockpit to roll up output by team and to map velocity
// developer stats → a known QA engineer.
export const QA_TEAM_MEMBERS = [
    {
        email: 'lana.kukharchyk@bstock.com',
        displayName: 'Lana Kukharchyk',
        aliases: ['Lana', 'lana.kukharchyk'],
        team: 'us',
    },
    { email: 'olga@bstock.com', displayName: 'Olga Chmikhun', aliases: ['Olga', 'olga.chmikhun'], team: 'us' },
    { email: 'gavin.grooms@bstock.com', displayName: 'Gavin Grooms', aliases: ['Gavin', 'gavin.grooms'], team: 'us' },
    {
        email: 'anthony.lombardo@bstock.com',
        displayName: 'Anthony Lombardo',
        aliases: ['anthony.lombardo'],
        team: 'us',
    },
    {
        email: 'daria.kazhybay@bstock.com',
        displayName: 'Daria Kazhybay',
        aliases: ['Daria', 'daria.kazhybay'],
        team: 'us',
    },
    {
        email: 'lam.nguyen.tung@bstock.com',
        displayName: 'Lam Nguyen Tung',
        aliases: ['Lam', 'lam.nguyen.tung'],
        team: 'evizi',
    },
    {
        email: 'vinh.tran.cong@bstock.com',
        displayName: 'Vinh Tran',
        aliases: ['Vinh', 'vinh.tran.cong'],
        team: 'evizi',
    },
    {
        email: 'thanh.nguyen@bstock.com',
        displayName: 'Thanh Nguyen',
        aliases: ['Thanh', 'Than', 'thanh.nguyen'],
        team: 'evizi',
    },
    { email: 'sohil@bstock.com', displayName: 'Sohil Sethi', aliases: ['Sohil', 'sohil'], team: 'us' },
]

export const QA_TEAM_MEMBER_NAMES = QA_TEAM_MEMBERS.map((member) => member.displayName)

function matchesQaIdentity(raw, candidate) {
    const value = (raw || '').toLowerCase().trim()
    const identity = (candidate || '').toLowerCase().trim()
    if (!value || !identity) return false

    if (identity.includes('@')) {
        return value === identity || value.includes(identity)
    }
    if (identity.includes(' ') || identity.includes('.') || identity.includes('_')) {
        return value === identity || value.includes(identity)
    }
    return (
        value === identity ||
        value.startsWith(`${identity} `) ||
        value.startsWith(`${identity}.`) ||
        value.startsWith(`${identity}_`) ||
        value.includes(` ${identity} `) ||
        value.endsWith(` ${identity}`)
    )
}

// Resolve a raw name/email/alias to a known QA team member (or null).
export function getQaTeamMember(raw) {
    const value = (raw || '').toLowerCase().trim()
    if (!value) return null
    for (const member of QA_TEAM_MEMBERS) {
        const identities = [member.email, member.displayName, ...member.aliases]
        if (identities.some((identity) => matchesQaIdentity(value, identity))) return member
    }
    return null
}

// Shared avatar palette for the Impact cockpit (mirrors qa-live avatarUtils).
export const AVATAR_PALETTE = ['#3B82F6', '#EC4899', '#10B981', '#F59E0B', '#8B5CF6', '#06B6D4', '#EF4444', '#6366F1']

// QA score penalty configuration — matches qa-live appConfig.ts so the Sprint Score
// gauge reaches the same number as qa-live for the same sprint data.
export const QA_SCORE_PENALTIES = {
    HOTFIX_RELEASE: 10,
    PATCH_RELEASE: 0,
    DEFECT_RATE_HIGH_THRESHOLD: 0.5,
    DEFECT_RATE_HIGH_PENALTY: 10,
    DEFECT_RATE_LOW_THRESHOLD: 0.3,
    DEFECT_RATE_LOW_PENALTY: 5,
    COMPLETION_RATE_CRITICAL_THRESHOLD: 0.7,
    COMPLETION_RATE_CRITICAL_PENALTY: 10,
    COMPLETION_RATE_LOW_THRESHOLD: 0.85,
    COMPLETION_RATE_LOW_PENALTY: 5,
}

export const QA_SCORE_THRESHOLDS = {
    GREEN: 85,
    YELLOW: 70,
}

// Pulse uses a slightly different color palette than the host dashboard's COLORS object,
// kept here so individual component colors (priority badges, status colors) survive port.
export const PULSE_COLORS = {
    cardBg: '#1e293b',
    border: '#334155',
    bgInner: '#0f172a',
    text: '#f1f5f9',
    textMuted: '#94a3b8',
    textSubtle: '#64748b',
    primary: '#3b82f6',
    success: '#10b981',
    warning: '#f59e0b',
    danger: '#ef4444',
}

// Priority badge colors — preserved from qa-live light theme so red/orange severity
// reads correctly against the dark card background.
export const PRIORITY_CONFIG = {
    P0: { label: 'P0', color: '#FEE2E2', textColor: '#EF4444' },
    P1: { label: 'P1', color: '#FFEDD5', textColor: '#F59E0B' },
    P2: { label: 'P2', color: '#CFFAFE', textColor: '#06B6D4' },
    P3: { label: 'P3', color: '#DBEAFE', textColor: '#3B82F6' },
}

export const PRIORITIES = ['P0', 'P1', 'P2', 'P3']
